import React, { forwardRef, useMemo, } from 'react';
import styled, { css } from 'styled-components';
import { Box } from '../Box';
import { Icon } from '../Icon';
import { Field } from '../fields/Field';
import { StyledFrontIcon } from '../fields/components/CommonInput';
import { theme } from '../theme';
import { useUniqueId } from '../utils/id';
import { useControllableState } from '../utils/useControlledState';
export const Dropdown = forwardRef(function Dropdown({ id: idProp, placeholder, showDefaultOption = false, customDefaultOption, name, value: valueProp, defaultValue, disabled = false, list, onSelect, error = false, onInputChange, onBlur, frontIcon, fallbackStyle, ...fieldProps }, ref) {
    const [value, setValue] = useControllableState({
        initialState: defaultValue,
        stateProp: valueProp,
    });
    const id = useUniqueId(idProp);
    const hasOptGroups = list.findIndex((item) => !!item.optionGroupLabel) !== -1;
    const dropdownItemsGroups = useMemo(() => {
        const itemsPerGroupLabel = new Map();
        list.forEach((item) => {
            const key = item.optionGroupLabel ?? '';
            const group = itemsPerGroupLabel.get(key) ?? [];
            group.push(item);
            itemsPerGroupLabel.set(key, group);
        });
        return Array.from(itemsPerGroupLabel.values());
    }, [list]);
    const defaultOptionLabel = () => {
        if (!showDefaultOption) {
            return placeholder;
        }
        return customDefaultOption ?? 'Select an option';
    };
    return (React.createElement(Field, { ...fieldProps, htmlFor: id, error: error },
        React.createElement(Box, { flex: true, alignItems: "center", style: { position: 'relative' } },
            frontIcon && (React.createElement(StyledFrontIcon, { "$disabled": disabled, render: frontIcon, color: "sesame" })),
            React.createElement(StyledSelect, { id: id, disabled: disabled || list.length < 1, onChange: (event) => {
                    const value = event.currentTarget.value;
                    onSelect?.(value);
                    onInputChange?.(event);
                    setValue(value);
                }, "$error": error, ref: ref, onBlur: onBlur, name: name, "$frontIcon": frontIcon, "$fallbackStyle": fallbackStyle, value: value ?? '' },
                hasOptGroups ? (React.createElement("optgroup", { label: defaultOptionLabel() },
                    React.createElement("option", { value: "", hidden: !showDefaultOption, disabled: true }, defaultOptionLabel()))) : (React.createElement("option", { value: "", hidden: !showDefaultOption, disabled: true }, defaultOptionLabel())),
                dropdownItemsGroups.map((groupItems, i) => hasOptGroups ? (React.createElement("optgroup", { key: i, label: groupItems[0].optionGroupLabel ?? 'Other' }, groupItems.map((el, j) => (React.createElement("option", { key: `${i}-${j}`, value: el.value }, el.label))))) : (groupItems.map((el, j) => (React.createElement("option", { key: j, value: el.value }, el.label)))))),
            React.createElement(Caret, null,
                React.createElement(Icon, { render: "caret", color: "marzipan", size: 24 })))));
});
const resetSelect = css `
  border: none;
  border-radius: 0;
  font-size: 16px;
  appearance: none; /* remove default arrow */
  outline: none;
`;
const StyledSelect = styled.select `
  ${resetSelect}
  width: 100%;
  height: 32px;
  color: ${({ value }) => value === '' ? theme.colors.sesame : theme.colors.liquorice};
  cursor: pointer;
  background-color: ${({ $fallbackStyle }) => $fallbackStyle ? theme.colors.custard : theme.colors.cream};

  border-radius: 12px;
  padding: 18px 14px;
  border: 2px solid
    ${({ $error }) => ($error ? theme.colors.strawberry : theme.colors.oatmeal)};
  height: auto;

  ${({ $frontIcon }) => $frontIcon &&
    $frontIcon != '' &&
    `
      padding-left: 42px;
    `}

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  ${({ $error }) => !$error &&
    css `
      :not(:disabled) {
        &:hover,
        &:focus,
        &:focus-visible,
        &:checked {
          border-color: ${theme.colors.marzipan}
      }
    `}
`;
const Caret = styled.div `
  position: absolute;
  z-index: 1;
  right: 15px;
  pointer-events: none;
  color: ${theme.colors.marzipan};
`;
//# sourceMappingURL=Dropdown.js.map